import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { getClientProducts } from 'api/generated/client-products/client-products';
import { ClientProductDtoType } from 'api/generated/schemas';
import type { ClientProductDto } from 'api/generated/schemas';

import { useCurrentUser } from 'hooks/api/use-current-user';

import type { ExtendWithOption } from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

const CLIENT_PRODUCTS_PAGE_SIZE = 1000;

const EXTEND_WITH_PRODUCT_TYPE: Record<ExtendWithOption, ClientProductDtoType> =
  {
    prospect: ClientProductDtoType.PROSPECT_VIEW,
    'financial-aid': ClientProductDtoType.FINANCIAL_AID_VIEW,
    retention: ClientProductDtoType.RETENTION_VIEW,
  };

export const clientMonitoringProductsKeys = {
  all: ['clientMonitoringProducts'] as const,
  byClient: (userId: string, clientId: string) =>
    [...clientMonitoringProductsKeys.all, userId, clientId] as const,
};

export interface UseClientMonitoringProductsResult {
  loading: boolean;
  error: unknown;
  // Extend With options are fully hidden (not shown as locked/upsell) when the
  // client isn't subscribed to the matching product.
  entitledExtendWithOptions: ExtendWithOption[];
  refetch: () => void;
}

export const useClientMonitoringProducts = (
  clientId: string | undefined
): UseClientMonitoringProductsResult => {
  const currentUser = useCurrentUser();
  const userId = currentUser.data?.oktaUserId;

  const productsQuery: UseQueryResult<ClientProductDto[], Error> = useQuery({
    queryKey: clientMonitoringProductsKeys.byClient(
      userId ?? '',
      clientId ?? ''
    ),
    queryFn: () =>
      getClientProducts(userId as string, Number(clientId), {
        size: CLIENT_PRODUCTS_PAGE_SIZE,
      }),
    enabled: !!userId && !!clientId,
  });

  const productsData = productsQuery.data;

  // Recompute only when the query result identity actually changes — an
  // inline `.filter()` on every render would give the consumer's
  // `useEffect(..., [entitledExtendWithOptions])` a new array reference on
  // every render and never let it settle.
  const entitledExtendWithOptions = useMemo(() => {
    const subscribedTypes = new Set(
      (productsData ?? []).map((product) => product.type)
    );

    return (Object.keys(EXTEND_WITH_PRODUCT_TYPE) as ExtendWithOption[]).filter(
      (option) => subscribedTypes.has(EXTEND_WITH_PRODUCT_TYPE[option])
    );
  }, [productsData]);

  return {
    loading: productsQuery.isLoading,
    error: productsQuery.error,
    entitledExtendWithOptions,
    refetch: () => {
      productsQuery.refetch();
    },
  };
};

export default useClientMonitoringProducts;
